import React from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useInternalSupportCases,
  useMergeSupportCase,
} from '../../../data/support/useSupport';
import { SupportCase } from '../../../types/support.types';

interface MergeCaseDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCase: SupportCase;
}

const MergeCaseDialog: React.FC<MergeCaseDialogProps> = ({
  open,
  onClose,
  sourceCase,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [selectedCase, setSelectedCase] = React.useState<SupportCase | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  const casesQuery = useInternalSupportCases(
    search ? { search, size: 20 } : { size: 20 },
  );
  const cases = (casesQuery.data || []).filter((c) => c.id !== sourceCase.id);

  const merge = useMergeSupportCase(sourceCase.id);

  const handleSubmit = () => {
    if (!selectedCase) return;
    setError(null);
    merge.mutate(selectedCase.id, {
      onSuccess: () => {
        onClose();
        navigate(`/internal/support/cases/${selectedCase.id}`);
      },
      onError: (err: any) => {
        const detail = err?.response?.data?.detail || 'Failed to merge case';
        setError(detail);
      },
    });
  };

  const handleClose = () => {
    setSelectedCase(null);
    setSearch('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge Case #{sourceCase.case_number}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All threads and notes from this case will be moved to the target case.
          This case will be closed.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Autocomplete
          options={cases}
          getOptionLabel={(opt) => `#${opt.case_number} — ${opt.subject}`}
          value={selectedCase}
          onChange={(_, value) => setSelectedCase(value)}
          onInputChange={(_, value) => setSearch(value)}
          loading={casesQuery.isLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Case"
              size="small"
              placeholder="Search by case number or subject..."
              sx={{ mt: 1 }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {casesQuery.isLoading ? (
                        <CircularProgress size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={!selectedCase || merge.isPending}
        >
          {merge.isPending ? 'Merging...' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeCaseDialog;
